import {readFile} from "./read-file.util";
import Papa from "papaparse"
import * as ExcelJS from "exceljs";
import * as XLSX from 'xlsx';

export const removeHeaderMetaData = (csv: string): string =>{
	const regex = /\(\(info\)[^)]*\)/g;
	return csv.replace(regex, '').trim();
}
export function addInfoToHeaderString(info: string){
	return `((info) ${info})`;
}

export const  readCsvAndRemoveMetadata = async (blob: Blob): Promise<string> => {
	let csv = await readFile(blob);
	csv = removeCsvMetaData(csv);
	return csv;
}


export const removeCsvMetaData = (csv: string): string => {
  let cleanedCsv = csv.trim();

  if (cleanedCsv.charCodeAt(0) === 0xFEFF) {
    cleanedCsv = cleanedCsv.slice(1);
  }

  if (cleanedCsv.startsWith('sep=')) {
    const lineEndIndex = Math.min(
      cleanedCsv.indexOf('\n') !== -1 ? cleanedCsv.indexOf('\n') : Infinity,
      cleanedCsv.indexOf('\r') !== -1 ? cleanedCsv.indexOf('\r') : Infinity
    );
    if (lineEndIndex !== Infinity) {
      cleanedCsv = cleanedCsv.substring(lineEndIndex + 1);
    }
  }
  return cleanedCsv;
};

export const csvToJson = (csvText): unknown[] => {
	let cleanedCsv = removeCsvMetaData(csvText);
	cleanedCsv = removeHeaderMetaData(cleanedCsv);
	const parseResult = Papa.parse(cleanedCsv, {
	  skipEmptyLines: true,
	  header: true,
	  transform: (value) => value.trim(),
	  transformHeader: (header) => header.trim(),
	  delimiter: ';'
	});
  
	const validHeaders = parseResult.meta.fields.filter(
	  (header) => header && header.trim() !== ""
	);

	const cleanedData = parseResult.data.filter((row) =>
		Object.values(row).some((value: string) => value.trim() !== "")
	  );
	
	return cleanedData.map((row) => {
	const formattedRow = {};

	validHeaders.forEach((header) => {
	  const formattedKey = formatName(header, { dontRemoveRepeatSufix: true}) || header;
	  formattedRow[formattedKey] = row[header];
	});

	return formattedRow;
	});
  };

export async function normalizeExcelBlob(blob: Blob, fileName: string): Promise<File> {
	const arrayBuffer = await new Promise<ArrayBuffer>((resolve, reject) => {
	  const reader = new FileReader();
	  reader.onload = () => resolve(reader.result as ArrayBuffer);
	  reader.onerror = () => reject(new Error('Failed to read the Blob as ArrayBuffer'));
	  reader.readAsArrayBuffer(blob);
	});
  
	const originalWorkbook = new ExcelJS.Workbook();
	await originalWorkbook.xlsx.load(arrayBuffer);
  
	const originalWorksheet = originalWorkbook.worksheets[0];
  
	let sheetName = fileName.replace(/\.\w+$/, '');
	sheetName = sanitizeWorksheetName(sheetName);
	const newWorkbook = new ExcelJS.Workbook();
	const newWorksheet = newWorkbook.addWorksheet(sheetName);
  
	const processCellValue = (value: any): string => {
		if (value === undefined || value === null) return '';
		if (typeof value === 'string') return value;
		if (typeof value === 'number') return value.toString();
		if (typeof value === 'boolean') return value ? 'true' : 'false';
		if (value instanceof Date) return value.toISOString();
		if (value.richText) return value.richText.map((text: any) => text.text).join('');
		return JSON.stringify(value);
	  };
	  
	originalWorksheet.eachRow((row, rowIndex) => {
		const newRow = newWorksheet.getRow(rowIndex);
		row.eachCell((cell, colIndex) => {
		  newRow.getCell(colIndex).value = processCellValue(cell.value);
		});
		newRow.commit();
	});
	if (newWorksheet.columnCount > 0) {
	  addAutofilter(newWorksheet.getRow(1));
	}
  
	const headerRow = newWorksheet.getRow(1);
	addStyleToHeader(headerRow);
	forceTextRepresentationOnColumns(newWorksheet);
  
	const buffer = await newWorkbook.xlsx.writeBuffer();
  
	return new File([buffer], fileName, {
	  type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	});
}

export async function convertJsonArrayToXlsx(
	jsonData: any[] | string, 
	fileName: string,
	metaInfo?: Record<string, string>
  ): Promise<File> {
	const jsonArray = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
	
	let headers: string[] = [];
	let mappedData = [];
	
	if (jsonArray.length > 0) 
	headers = Array.from(
	  jsonArray.reduce((headerSet, obj) => {
		Object.keys(obj).forEach(key => headerSet.add(key));
		return headerSet;
	  	}, new Set<string>())
	 );
	
	mappedData = jsonArray;
	
	
	const sheetName = fileName.replace(/\.\w+$/, '');
	return createXlsxFile(mappedData, headers, sheetName, metaInfo);
}

export async function convertCsvFileToXlsx(csvFile: File): Promise<File> {
	return convertCsvBlobToXlsx(csvFile, csvFile.name);
}

export async function convertCsvBlobToXlsx(csvBlob: Blob, fileName: string): Promise<File> {
	const sheetName = fileName.replace('.csv', '');
	const csvData = await readFile(csvBlob);
	const cleanedCsv = removeCsvMetaData(csvData);
	const sameHeaderCounter  = {}
	const parsedData = Papa.parse(cleanedCsv, {
		skipEmptyLines: true,
		header: true,
		transform: (value) => value.trim().replace(/\r/g, ""),
		transformHeader: (header) => {
			const headerCount = (sameHeaderCounter[header] || 0) + 1;
			sameHeaderCounter[header] = headerCount;
			return headerCount > 1 ? `${header}${" ".repeat(headerCount)}` : header;
		},
		delimiter: ';',
	  });
	
	const validHeaders = parsedData.meta.fields.filter((header) => header && header.trim() !== '');
	const cleanedData = parsedData.data.map((row) => {
		const cleanedRow = {};
		validHeaders.forEach((header) => {
		  cleanedRow[header] = row[header];
		});
		return cleanedRow;
	  });
	return createXlsxFile(cleanedData, validHeaders, sheetName);
}


async function createXlsxFile(data: any[], headers: string[], sheetName: string, metaInfo?: Record<string, string>): Promise<File> {
	sheetName = sanitizeWorksheetName(sheetName)
	const workbook = new ExcelJS.Workbook();
	const worksheet = workbook.addWorksheet(sheetName);
	if (metaInfo && Object.keys(metaInfo).length > 0) {
		const metaInfoRows = Object.entries(metaInfo).map(([key, value]) => [key, value]);
		worksheet.addRows(metaInfoRows);
	}
	
	const headerRow = worksheet.addRow(headers);
	const rows = data.map((row) => headers.map((header) => {
		const hasRow = row[header]
		if(hasRow) {
			return row[header].toString() || ""
		}
		return ""
	}));
	worksheet.addRows(rows);
	addAutofilter(headerRow);
	addStyleToHeader(headerRow);
	forceTextRepresentationOnColumns(worksheet);
	headerRow.eachCell((cell) => {
        if (typeof cell.value === 'string') {
            cell.value = cell.value.trim();
        }
    });
	const buffer = await workbook.xlsx.writeBuffer();
	return new File([buffer], `${sheetName}.xlsx`, {
	  type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	});
}

export const  convertXlsxFileToCsvFile = async (xlsxFile: File):  Promise<File> => {
	const fileReader = new FileReader();

	const excelData: ArrayBuffer = await new Promise<ArrayBuffer>((resolve, reject) => {
	  fileReader.onload = (event: any) => resolve(event.target.result as ArrayBuffer);
	  fileReader.onerror = () => reject(new Error('Cannot read XLSX'));
	  fileReader.readAsArrayBuffer(xlsxFile);
	});

	const workbook = XLSX.read(excelData, { type: 'array' });
	const firstSheetName = workbook.SheetNames[0];
	const worksheet = workbook.Sheets[firstSheetName];
	let csvContent = XLSX.utils.sheet_to_csv(worksheet, { FS: ';' })
	csvContent = removeHeaderMetaData(csvContent);

    const isoBuffer = stringToIsoBuffer(csvContent)
    const outputCsvFile = new File([isoBuffer], xlsxFile.name.replace(/\.(xls|xlsx)$/, '.csv'), {
        type: 'text/csv',
    });

    return outputCsvFile;
}

export function stringToIsoBuffer(str: string): Uint8Array {
    const buffer = new Uint8Array(str.length);
    for (let i = 0; i < str.length; i++) {
        buffer[i] = str.charCodeAt(i) & 0xff;
    }
    return buffer;
}

export const csvHeaders = ((csv): any[] => {
	csv = removeCsvMetaData(csv);
	const cleanedCsv = removeCsvMetaData(csv);
	const parseResult = Papa.parse(cleanedCsv, {
	  skipEmptyLines: true,
	  preview: 1, 
	  delimiter: ';',
	});
	if (parseResult.data.length === 0 || !Array.isArray(parseResult.data[0])) {
	  return []; 
	}
	return parseResult.data[0].map((header: string) => header.trim());
})

export const formatName = (name: string, options?: {dontRemoveRepeatSufix:boolean}): string => {
	name = removeHeaderMetaData(name);
	if(!options ||  options.dontRemoveRepeatSufix !== true) {
		const repetSufix = /_\d+$/
        name = name.replace(repetSufix, "").trim();
    }
	let words = name.replace(/\r/g, "").replace(/%/g, "").trim().split(' ');
	if(words.length == 1) {
		words[0] = words[0].toLowerCase();
	}
	const formattedName = words.map((word, index) => {
		if (index === 0) {
			return word.toLowerCase();
		}
		return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
	}).join('');
	return formattedName;
}

export const sanitizeWorksheetName = (name: string): string => {
    return name.replace(/[*?:\\/[\]]/g, '_');
};


const addStyleToHeader = (headerRow: ExcelJS.Row) => {
	headerRow.eachCell((cell) => {
		cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
		cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0070C0' } };
		cell.alignment = { vertical: 'middle', horizontal: 'left' };
		cell.border = {
		  top: { style: 'thin' },
		  left: { style: 'thin' },
		  bottom: { style: 'thin' },
		  right: { style: 'thin' },
		};
	});
}

const forceTextRepresentationOnColumns = (worksheet: ExcelJS.Worksheet) => {
	worksheet.columns.forEach((column) => {
		column.numFmt = '@';
	});
}

const addAutofilter = (headerRow:  ExcelJS.Row) => {
	headerRow.worksheet.autoFilter = {
		from: { row: headerRow['_number'], column: 1 },
		to: { row: headerRow['_number'], column: headerRow.worksheet.columnCount },
	};
}